import { supabase } from "@/integrations/supabase/client";

export interface ResumeAnalysis {
  id: string;
  user_id: string;
  original_filename: string;
  file_url: string;
  file_size: number;
  job_description?: string;
  job_title?: string;
  industry?: string;
  targeting_method: 'description' | 'manual';
  extracted_text?: string;
  analysis_results: any;
  created_at: string;
  updated_at: string;
}

export interface UploadResumeData {
  resume: File;
  jobDescription?: string;
  jobTitle?: string;
  industry?: string;
  targetingMethod: 'description' | 'manual';
}

export interface Resume {
  id: string;
  user_id: string;
  title: string;
  content?: string;
  file_name?: string;
  file_type?: string;
  original_file_url?: string;
  original_file_path?: string;
  parsed_resume_data?: any;
  processing_status?: string;
  word_document_url?: string;
  word_document_path?: string;
  created_at: string;
  updated_at: string;
}

export class ResumeService {
  // Resume library methods
  static async saveResume(userId: string, title: string, content?: string, fileName?: string, fileType?: string): Promise<{ resume: Resume | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('resumes' as any)
        .insert({
          user_id: userId,
          title,
          content,
          file_name: fileName,
          file_type: fileType,
          processing_status: 'uploaded'
        })
        .select()
        .single();

      return { resume: data as any, error };
    } catch (error) {
      return { resume: null, error };
    }
  }

  static async saveResumeWithFile(userId: string, title: string, file: File, parsedData?: any): Promise<{ resume: Resume | null; error: any }> {
    try {
      // Upload original file to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}/${Date.now()}.${fileExt}`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('resumes')
        .upload(fileName, file, {
          contentType: file.type,
          upsert: false
        });

      if (uploadError) {
        return { resume: null, error: uploadError };
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('resumes')
        .getPublicUrl(fileName);

      // Save resume record to database
      const { data, error } = await supabase
        .from('resumes' as any)
        .insert({
          user_id: userId,
          title,
          file_name: file.name,
          file_type: file.type,
          original_file_url: publicUrl,
          original_file_path: fileName,
          parsed_resume_data: parsedData,
          processing_status: parsedData ? 'parsed' : 'uploaded'
        })
        .select()
        .single();

      if (error) {
        // Clean up uploaded file if database insert fails
        await supabase.storage.from('resumes').remove([fileName]);
        return { resume: null, error };
      }

      return { resume: data as any, error: null };
    } catch (error) {
      return { resume: null, error };
    }
  }

  static async getUserResumes(userId: string): Promise<{ resumes: Resume[]; error: any }> {
    const { data, error } = await supabase
      .from('resumes' as any)
      .select('*')
      .eq('user_id', userId)
      .is('original_match_score', null) // Only get regular uploaded resumes, not tailored ones
      .is('original_resume_id', null) // Also exclude resumes that reference another resume
      .order('updated_at', { ascending: false });

    return { resumes: (data as any) || [], error };
  }

  static async getResume(id: string): Promise<{ resume: Resume | null; error: any }> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { resume: null, error: { message: 'User not authenticated' } };
    }

    const { data, error } = await supabase
      .from('resumes' as any)
      .select('*')
      .eq('id', id)
      .eq('user_id', user.id) // CRITICAL: Only allow access to user's own resumes
      .single();

    return { resume: data as any, error };
  }

  static async updateResume(id: string, title: string, content: string): Promise<{ resume: Resume | null; error: any }> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { resume: null, error: { message: 'User not authenticated' } };
    }

    const { data, error } = await supabase
      .from('resumes' as any)
      .update({ title, content, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', user.id) // CRITICAL: Only allow updates to user's own resumes
      .select()
      .single();

    return { resume: data as any, error };
  }

  static async deleteResume(id: string): Promise<{ error: any }> {
    // First get the resume to check for files to delete
    const { resume, error: getError } = await this.getResume(id);
    
    if (getError || !resume) {
      return { error: getError || new Error('Resume not found') };
    }

    // Delete original file from storage if it exists
    if (resume.original_file_path) {
      const { error: storageError } = await supabase.storage
        .from('resumes')
        .remove([resume.original_file_path]);

      if (storageError) {
        console.error('Failed to delete original file from storage:', storageError);
      }
    }

    // Delete Word document from storage if it exists
    if (resume.word_document_path) {
      const { error: wordStorageError } = await supabase.storage
        .from('word-documents')
        .remove([resume.word_document_path]);

      if (wordStorageError) {
        console.error('Failed to delete Word document from storage:', wordStorageError);
      }
    }

    // Delete from database
    const { error } = await supabase
      .from('resumes' as any)
      .delete()
      .eq('id', id);

    return { error };
  }

  static async updateWordDocument(id: string, wordDocumentUrl: string, wordDocumentPath: string): Promise<{ resume: Resume | null; error: any }> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { resume: null, error: { message: 'User not authenticated' } };
    }

    const { data, error } = await supabase
      .from('resumes' as any)
      .update({ 
        word_document_url: wordDocumentUrl,
        word_document_path: wordDocumentPath,
        processing_status: 'word_generated',
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .eq('user_id', user.id) // CRITICAL: Only allow updates to user's own resumes
      .select()
      .single();

    return { resume: data as any, error };
  }

  static async updateProcessingStatus(id: string, status: string): Promise<{ resume: Resume | null; error: any }> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { resume: null, error: { message: 'User not authenticated' } };
    }

    const { data, error } = await supabase
      .from('resumes' as any)
      .update({ 
        processing_status: status,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .eq('user_id', user.id) // CRITICAL: Only allow updates to user's own resumes
      .select()
      .single();

    return { resume: data as any, error };
  }

  // Resume analysis methods
  static async uploadResume(userId: string, data: UploadResumeData): Promise<{ analysis: ResumeAnalysis | null; error: any }> {
    try {
      // Upload file to storage
      const fileExt = data.resume.name.split('.').pop();
      const fileName = `${userId}/${Date.now()}.${fileExt}`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('resumes')
        .upload(fileName, data.resume, {
          contentType: data.resume.type,
          upsert: false
        });

      if (uploadError) {
        return { analysis: null, error: uploadError };
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('resumes')
        .getPublicUrl(fileName);

      // Save analysis record to database
      const { data: analysisData, error: dbError } = await supabase
        .from('resume_analyses')
        .insert({
          user_id: userId,
          original_filename: data.resume.name,
          file_url: publicUrl,
          file_size: data.resume.size,
          job_description: data.jobDescription,
          job_title: data.jobTitle,
          industry: data.industry,
          targeting_method: data.targetingMethod,
          analysis_results: {}
        })
        .select()
        .single();

      if (dbError) {
        // Clean up uploaded file if database insert fails
        await supabase.storage.from('resumes').remove([fileName]);
        return { analysis: null, error: dbError };
      }

      return { analysis: analysisData as ResumeAnalysis, error: null };
    } catch (error) {
      return { analysis: null, error };
    }
  }

  static async createResumeAnalysis(userId: string, data: {
    filename: string;
    extractedText: string;
    jobDescription?: string;
    jobTitle?: string;
    industry?: string;
    targetingMethod: 'description' | 'manual';
  }): Promise<{ analysis: ResumeAnalysis | null; error: any }> {
    try {
      // Save analysis record to database with extracted text
      const { data: analysisData, error: dbError } = await supabase
        .from('resume_analyses')
        .insert({
          user_id: userId,
          original_filename: data.filename,
          file_url: '', // No file URL since we're using extracted text
          file_size: 0, // No file size since we're using extracted text
          job_description: data.jobDescription,
          job_title: data.jobTitle,
          industry: data.industry,
          targeting_method: data.targetingMethod,
          extracted_text: data.extractedText,
          analysis_results: {}
        })
        .select()
        .single();

      if (dbError) {
        return { analysis: null, error: dbError };
      }

      return { analysis: analysisData as ResumeAnalysis, error: null };
    } catch (error) {
      return { analysis: null, error };
    }
  }

  static async getUserResumeAnalyses(userId: string): Promise<{ analyses: ResumeAnalysis[]; error: any }> {
    const { data, error } = await supabase
      .from('resume_analyses')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    return { analyses: (data as ResumeAnalysis[]) || [], error };
  }

  static async getResumeAnalysis(id: string): Promise<{ analysis: ResumeAnalysis | null; error: any }> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { analysis: null, error: { message: 'User not authenticated' } };
    }

    const { data, error } = await supabase
      .from('resume_analyses')
      .select('*')
      .eq('id', id)
      .eq('user_id', user.id) // CRITICAL: Only allow access to user's own analyses
      .single();

    return { analysis: data as ResumeAnalysis, error };
  }

  static async updateAnalysisResults(id: string, results: any): Promise<{ analysis: ResumeAnalysis | null; error: any }> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { analysis: null, error: { message: 'User not authenticated' } };
    }

    const { data, error } = await supabase
      .from('resume_analyses')
      .update({ 
        analysis_results: results,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .eq('user_id', user.id) // CRITICAL: Only allow updates to user's own analyses
      .select()
      .single();

    return { analysis: data as ResumeAnalysis, error };
  }

  static async deleteResumeAnalysis(id: string): Promise<{ error: any }> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { error: { message: 'User not authenticated' } };
    }

    // First get the analysis to verify ownership and get file info
    const { analysis, error: getError } = await this.getResumeAnalysis(id);
    
    if (getError || !analysis) {
      return { error: getError || new Error('Analysis not found') };
    }

    // Extract file path from URL if it exists
    if (analysis.file_url) {
      try {
        const url = new URL(analysis.file_url);
        const filePath = url.pathname.split('/').slice(-2).join('/'); // Get last two parts (userId/filename)

        // Delete from storage
        const { error: storageError } = await supabase.storage
          .from('resumes')
          .remove([filePath]);

        if (storageError) {
          console.error('Failed to delete file from storage:', storageError);
          // Continue with database deletion even if storage fails
        }
      } catch (error) {
        console.error('Error parsing file URL:', error);
      }
    }

    // Delete from database - getResumeAnalysis already verified user ownership
    const { error: dbError } = await supabase
      .from('resume_analyses')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id); // Double-check user ownership

    return { error: dbError };
  }
}